import { useState, useEffect } from 'react';
import strategyService, { type StrategyOverview, type AsIsMetrics, type GoalItem, type ActionCategory, type DashboardStatus, type CompetitionData } from '../../../services/strategyService';
import { calculateDaysRemaining, getMillisecondsUntilMidnight, format90DayRange, formatUnlockDate } from '../../../utils/dateUtils';
import SetupRequiredOverlay from './SetupRequiredOverlay';

interface StrategyDashboardProps {
  onTooltipShow: (title: string, content: string) => void;
  setActiveSection?: (section: string) => void;
  dashboardStatus?: DashboardStatus | null;
}

export default function StrategyDashboard({ onTooltipShow, setActiveSection, dashboardStatus }: StrategyDashboardProps) {
  const [overview, setOverview] = useState<StrategyOverview | null>(null);
  const [asIsMetrics, setAsIsMetrics] = useState<AsIsMetrics | null>(null);
  const [competitionData, setCompetitionData] = useState<CompetitionData | null>(null);
  const [goals, setGoals] = useState<GoalItem[]>([]);
  const [actionPlan, setActionPlan] = useState<ActionCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState<number>(0);

  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    if (!userId) {
      window.location.href = '/login';
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchOverview();
  }, [dashboardStatus, userId]);

  // Update days remaining when overview changes
  useEffect(() => {
    if (overview?.lockedUntil) {
      setDaysRemaining(calculateDaysRemaining(overview.lockedUntil));
    }
  }, [overview]);

  // Set up daily refresh at midnight
  useEffect(() => {
    if (!overview?.lockedUntil) return;

    const msUntilMidnight = getMillisecondsUntilMidnight();
    const timer = setTimeout(() => {
      // Recalculate days remaining at midnight
      setDaysRemaining(calculateDaysRemaining(overview.lockedUntil));

      // Set up daily interval
      const dailyInterval = setInterval(() => {
        setDaysRemaining(calculateDaysRemaining(overview.lockedUntil));
      }, 24 * 60 * 60 * 1000); // 24 hours

      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => clearTimeout(timer);
  }, [overview]);

  const fetchOverview = async () => {
    if (!userId) return;
    try {
      if (!overview) {
        setLoading(true);
      }
      // Always fetch overview (needed for Quick Stats cards)
      const overviewData = await strategyService.getStrategyOverview(userId);
      setOverview(overviewData);
      // Only fetch detailed section data when dashboard is fully ready
      if (dashboardStatus?.dashboard_ready) {
        setFetchingDetails(true);
      }
      setLoading(false); // Let the overview render while details load (now with shimmer)

      if (dashboardStatus?.dashboard_ready) {
        const [asIsRes, compRes, goalsRes, actionRes] = await Promise.allSettled([
          strategyService.getAsIsMetrics(userId),
          strategyService.getCompetitionData(userId),
          strategyService.getGoalsSnapshot(userId),
          strategyService.getActionPlanSnapshot(userId)
        ]);
        if (asIsRes.status === 'fulfilled') setAsIsMetrics(asIsRes.value);
        if (compRes.status === 'fulfilled') setCompetitionData(compRes.value);
        if (goalsRes.status === 'fulfilled') setGoals(goalsRes.value);
        if (actionRes.status === 'fulfilled') setActionPlan(actionRes.value);
      }
    } catch (err) {
      console.error('Error fetching strategy overview:', err);
      setError(err instanceof Error ? err.message : 'Failed to load overview');
    } finally {
      setLoading(false);
      setFetchingDetails(false);
    }
  };

  if (!userId) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading strategy overview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-4xl text-red-600 mb-2"></i>
        <h3 className="text-lg font-bold text-red-900 dark:text-red-300 mb-2">Error Loading Overview</h3>
        <p className="text-red-700 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!overview) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Strategy Overview</h2>
          <button
            onClick={() => onTooltipShow('Strategy Dashboard', 'Get a comprehensive view of your SEO strategy workflow and progress across all stages')}
            className="text-gray-400 hover:text-teal-500"
          >
            <i className="ri-information-line text-xl"></i>
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 flex items-center flex-wrap gap-2">
          Your complete SEO strategy workflow from keyword planning to action plan
          {overview.lockedUntil && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
              Active until {new Date(overview.lockedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </p>
      </div>

      {/* Quick Stats Snapshots */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-6 border border-blue-200 dark:border-blue-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <i className="ri-key-line text-white text-xl"></i>
            </div>
            <button
              onClick={() => onTooltipShow('Selected Keywords', 'These are your strategically chosen keywords for the 90-day strategy cycle. The selection is locked to ensure consistent tracking and measurement throughout the period.')}
              className="text-blue-400 hover:text-blue-600 transition-colors"
            >
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{overview.selectedKeywords}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Selected Keywords</p>
          <div className="mt-4 flex items-center text-xs text-blue-600 dark:text-blue-400">
            <i className="ri-lock-line mr-1"></i>
            <span>
              {overview.lockedUntil ? formatUnlockDate(overview.lockedUntil) : 'Strategy locked for 90 days'}
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl p-6 border border-green-200 dark:border-green-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <i className="ri-trophy-line text-white text-xl"></i>
            </div>
            <button
              onClick={() => onTooltipShow('Goals Identified', 'Track your progress toward your 90-day SEO goals. Monitor completion rate and see how many objectives you\'ve successfully accomplished.')}
              className="text-green-400 hover:text-green-600 transition-colors"
            >
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{overview.totalGoals}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Goals Identified</p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 font-medium">
              <i className="ri-checkbox-circle-line mr-1"></i>Active: {overview.activeGoals ?? overview.totalGoals - (overview.notApplicableGoals ?? 0)}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 font-medium">
              <i className="ri-eye-line mr-1"></i>Tracking: {overview.notApplicableGoals ?? 0}
            </span>
          </div>

        </div>


        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl p-6 border border-purple-200 dark:border-purple-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
              <i className="ri-task-line text-white text-xl"></i>
            </div>
            <button
              onClick={() => onTooltipShow('Action Items', 'Your SEO action plan contains tasks across On-Page, Off-Page, and Technical categories. Track pending tasks and monitor your implementation progress.')}
              className="text-purple-400 hover:text-purple-600 transition-colors"
            >
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{overview.actionItems}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Action Items</p>
          <div className="mt-4 flex items-center text-xs text-orange-600 dark:text-orange-400">
            <i className="ri-time-line mr-1"></i>
            <span>{overview.dueActionItems} pending tasks</span>
          </div>
        </div>
      </div>

      {/* Video Demo */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-lg flex items-center justify-center">
              <i className="ri-play-circle-line text-gray-400 dark:text-gray-500 text-xl"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-400 dark:text-gray-500">How Strategy Dashboard Works</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500">3:45 min tutorial</p>
            </div>
          </div>
          <button disabled className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-400 dark:text-gray-500 rounded-lg font-medium cursor-not-allowed whitespace-nowrap">
            <i className="ri-play-line mr-2"></i>Watch Demo
          </button>
        </div>
      </div>

      {/* 3-Month Strategy Snapshots */}
      {dashboardStatus && !dashboardStatus.dashboard_ready && setActiveSection ? (
        <SetupRequiredOverlay dashboardStatus={dashboardStatus} setActiveSection={setActiveSection} />
      ) : (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Next 3 Months Strategy Snapshot</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{format90DayRange(overview.lockedUntil)} • 90-Day Strategy Period</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* As-Is State Snapshot */}
          <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg border border-blue-200 dark:border-blue-700 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <i className="ri-file-list-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">As-Is State</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Current baseline metrics</p>
                </div>
              </div>
              <button
                onClick={() => onTooltipShow('As-Is State', 'Your current SEO baseline metrics including keyword positions, organic traffic, SERP visibility, and domain authority. This snapshot provides the starting point for measuring progress.')}
                className="text-blue-400 hover:text-blue-600 transition-colors"
              >
                <i className="ri-information-line text-lg"></i>
              </button>
            </div>

            <div className="grid grid-rows-4 gap-3 flex-1">
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg h-full">
                <span className="text-sm text-gray-700 dark:text-gray-300">Avg. Keyword Position</span>
                {fetchingDetails ? (
                  <div className="h-5 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                ) : (
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">#{asIsMetrics?.avgPosition || 'N/A'}</span>
                )}
              </div>
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg h-full">
                <span className="text-sm text-gray-700 dark:text-gray-300">Organic Traffic</span>
                {fetchingDetails ? (
                  <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                ) : (
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{asIsMetrics?.organicTraffic || 'N/A'}</span>
                )}
              </div>
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg h-full">
                <span className="text-sm text-gray-700 dark:text-gray-300">SERP Visibility</span>
                {fetchingDetails ? (
                  <div className="h-5 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                ) : (
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{asIsMetrics?.serpVisibility || 'N/A'}</span>
                )}
              </div>
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg h-full">
                <span className="text-sm text-gray-700 dark:text-gray-300">Competitive Analysis</span>
                {fetchingDetails ? (
                  <div className="h-5 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                ) : (
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{competitionData?.actualCompetitorsCount ?? 0}</span>
                )}
              </div>
            </div>

            <button
              onClick={() => setActiveSection?.('as-is-state')}
              className="w-full mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors whitespace-nowrap"
            >
              View Full As-Is State
            </button>
          </div>


          <div className="p-5 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/30 dark:to-teal-800/30 rounded-lg border border-teal-200 dark:border-teal-700 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                  <i className="ri-flag-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Goals</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">3-month objectives</p>
                </div>
              </div>
              <button
                onClick={() => onTooltipShow('Strategy Goals', 'Your 90-day SEO objectives with progress tracking. Primary goals are highlighted and represent your most critical targets for this quarter.')}
                className="text-teal-400 hover:text-teal-600 transition-colors"
              >
                <i className="ri-information-line text-lg"></i>
              </button>
            </div>

            <div className="grid grid-rows-6 gap-2.5 flex-1">
              {fetchingDetails ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="p-3 rounded-lg bg-white/60 dark:bg-gray-800/60 h-full flex items-center animate-pulse">
                    <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                ))
              ) : goals.length > 0 ? (
                goals.slice(0, 6).map((item, index) => (
                  <div key={index} className="p-3 rounded-lg bg-white dark:bg-gray-800 h-full flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.goal}</span>
                    {item.isPaused && (
                      <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 whitespace-nowrap">
                        (Paused)
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400 p-3">No goals set yet</div>
              )}
            </div>

            <button
              onClick={() => setActiveSection?.('goal-setting')}
              className="w-full mt-4 px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap"
            >
              View All Goals
            </button>
          </div>

          {/* Action Plan Snapshot */}
          <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-lg border border-purple-200 dark:border-purple-700 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <i className="ri-task-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Action Plan</h4>
                  {fetchingDetails ? (
                    <div className="h-3.5 w-28 bg-purple-200 dark:bg-purple-900/40 rounded animate-pulse mt-1"></div>
                  ) : (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {actionPlan.reduce((acc, cat) => acc + cat.tasks, 0)} tasks • {actionPlan.length > 0 && actionPlan.reduce((acc, cat) => acc + cat.tasks, 0) > 0
                        ? Math.round((actionPlan.reduce((acc, cat) => acc + cat.completed, 0) / actionPlan.reduce((acc, cat) => acc + cat.tasks, 0)) * 100)
                        : 0}% complete
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => onTooltipShow('Action Plan', 'Your comprehensive SEO implementation roadmap. Tasks are organized into Technical SEO, Content Creation, and Link Building categories. Track progress and prioritize pending work.')}
                className="text-purple-400 hover:text-purple-600 transition-colors"
              >
                <i className="ri-information-line text-lg"></i>
              </button>
            </div>

            <div className="grid grid-rows-3 gap-3 flex-1">
              {fetchingDetails ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg h-full flex items-center justify-between animate-pulse">
                    <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 w-6 bg-gray-100 dark:bg-gray-600 rounded"></div>
                  </div>
                ))
              ) : actionPlan.length > 0 ? (
                actionPlan.map((cat) => (
                  <div key={cat.category} className="p-3 bg-white dark:bg-gray-800 rounded-lg h-full flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{cat.category}</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{cat.tasks}</span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400 p-3">No action plan data available</div>
              )}
            </div>

            <button
              onClick={() => setActiveSection?.('action-plan-tracking')}
              className="w-full mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors whitespace-nowrap"
            >
              View Action Plan
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
